import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Heart, Crown, Lock, MapPin, Sparkles } from 'lucide-react';
import { premiumAPI, swipeAPI } from '../services/api';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function LikesPage() {
  const navigate = useNavigate();
  const [likes, setLikes] = useState([]);
  const [count, setCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLikes();
  }, []);

  const loadLikes = async () => {
    try {
      const response = await premiumAPI.getWhoLikedMe();
      setLikes(response.data.likes || []);
      setCount(response.data.count || 0);
      setIsPremium(response.data.is_premium || false);
    } catch (error) {
      console.error('Failed to load likes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLikeBack = async (userId) => {
    try {
      const response = await swipeAPI.action({
        target_user_id: userId,
        action: 'like'
      });
      
      if (response.data.is_match) {
        toast.success("It's a match! 🎉");
        // Remove from likes list
        setLikes(prev => prev.filter(l => l.user_id !== userId));
        setCount(prev => prev - 1);
      } else {
        toast.success('Liked back!');
        setLikes(prev => prev.filter(l => l.user_id !== userId));
        setCount(prev => prev - 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to like back');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="likes-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="px-6 py-4">
          <h1 className="font-heading text-2xl text-gold-gradient">Who Likes You</h1>
          <p className="text-gray-400 font-body text-sm mt-1">
            {count} {count === 1 ? 'person' : 'people'} liked your profile
          </p>
        </div>
      </header>

      <div className="px-4 py-6">
        {/* Not Premium - Show blur preview */}
        {!isPremium && count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="relative">
              {/* Blurred preview grid */}
              <div className="grid grid-cols-2 gap-3">
                {[...Array(Math.min(count, 4))].map((_, index) => (
                  <div
                    key={index}
                    className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-gold/20 to-pink-500/20 overflow-hidden relative"
                  >
                    <div className="absolute inset-0 backdrop-blur-xl bg-dark/30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Heart className="w-8 h-8 text-gold/50" fill="currentColor" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Upgrade overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-dark/60 rounded-2xl">
                <div className="text-center p-6">
                  <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-gold" />
                  </div>
                  <h2 className="font-heading text-xl text-white mb-2">
                    {count} {count === 1 ? 'Person Likes' : 'People Like'} You!
                  </h2>
                  <p className="text-gray-400 font-body text-sm mb-4">
                    Upgrade to Premium to see who likes you
                  </p>
                  <Button
                    onClick={() => navigate('/premium')}
                    className="btn-gold rounded-full px-6 py-2 font-body"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Premium - Show actual likes */}
        {isPremium && likes.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {likes.map((like, index) => (
              <motion.div
                key={like.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-elevated rounded-2xl overflow-hidden border border-white/10"
              >
                {/* Photo */}
                <div className="relative aspect-[3/4]">
                  <img
                    src={like.photo || 'https://via.placeholder.com/300x400'}
                    alt={like.name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Super like badge */}
                  {like.is_super_like && (
                    <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-1">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  )}
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  
                  {/* Info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="font-body font-semibold text-white text-lg">
                      {like.name}, {like.age}
                    </h3>
                    {like.location_country && (
                      <p className="text-gray-300 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {like.location_country}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Action button */}
                <div className="p-3">
                  <Button
                    onClick={() => handleLikeBack(like.user_id)}
                    className="w-full btn-gold rounded-full py-2 font-body text-sm"
                  >
                    <Heart className="w-4 h-4 mr-1" />
                    Like Back
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {count === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 rounded-full bg-dark-elevated flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-gray-500" />
            </div>
            <h2 className="font-heading text-xl text-white mb-2">No Likes Yet</h2>
            <p className="text-gray-400 font-body mb-6 max-w-xs mx-auto">
              Complete your profile and stay active to get more likes!
            </p>
            <Button
              onClick={() => navigate('/profile')}
              className="btn-gold rounded-full px-6 py-2 font-body"
            >
              Improve Profile
            </Button>
          </motion.div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
